import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { Eye, Users, Monitor, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function AnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [pages, setPages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/analytics/overview`, { params: { days }, withCredentials: true }),
      axios.get(`${API}/admin/analytics/timeline`, { params: { days }, withCredentials: true }),
      axios.get(`${API}/admin/analytics/pages`, { params: { days }, withCredentials: true }),
      axios.get(`${API}/admin/analytics/devices`, { params: { days }, withCredentials: true }),
      axios.get(`${API}/admin/analytics/referrers`, { params: { days }, withCredentials: true }),
    ])
      .then(([ov, tl, pg, dv, rf]) => {
        setOverview(ov.data);
        setTimeline(tl.data.timeline);
        setPages(pg.data.pages);
        setDevices(dv.data.devices);
        setReferrers(rf.data.referrers);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return <div className="p-8 text-slate-400">Loading analytics...</div>;
  }

  const stats = [
    { label: 'Total Page Views', value: overview?.total_views || 0, icon: Eye, color: 'blue' },
    { label: 'Today', value: overview?.today_views || 0, icon: Clock, color: 'emerald' },
    { label: 'Unique Visitors', value: overview?.unique_visitors || 0, icon: Users, color: 'purple' },
    { label: 'Devices Tracked', value: devices.length, icon: Monitor, color: 'amber' },
  ];

  const colorMap = { blue: 'bg-blue-500/10 text-blue-400', emerald: 'bg-emerald-500/10 text-emerald-400', purple: 'bg-purple-500/10 text-purple-400', amber: 'bg-amber-500/10 text-amber-400' };

  return (
    <div className="p-8" data-testid="analytics-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white" data-testid="analytics-title">Site Analytics</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-slate-800 border border-slate-700 rounded-lg text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500"
          data-testid="analytics-period"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-slate-800 rounded-xl p-5 border border-slate-700" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">{label}</span>
              <div className={`p-2 rounded-lg ${colorMap[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <div className="text-3xl font-bold text-white">{value}</div>
          </div>
        ))}
      </div>

      {/* Timeline Chart */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-6" data-testid="views-timeline">
        <h2 className="text-lg font-semibold text-white mb-4">Page Views</h2>
        <div className="h-64">
          {timeline.some(t => t.views > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="fillViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="views" stroke="#3b82f6" fill="url(#fillViews)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500">
              <p>No page view data yet. Views will appear as visitors browse your site.</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Pages */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="top-pages">
          <h2 className="text-lg font-semibold text-white mb-4">Top Pages</h2>
          {pages.length > 0 ? (
            <div className="space-y-3">
              {pages.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate mr-2">{p.page || '/'}</span>
                  <span className="text-blue-400 font-medium">{p.views}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No data yet</p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="device-breakdown">
          <h2 className="text-lg font-semibold text-white mb-4">Devices</h2>
          {devices.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={devices} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}>
                    {devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No data yet</p>
          )}
        </div>

        {/* Referrers */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="referrers">
          <h2 className="text-lg font-semibold text-white mb-4">Traffic Sources</h2>
          {referrers.length > 0 ? (
            <div className="space-y-3">
              {referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate mr-2">{r.source}</span>
                  <span className="text-blue-400 font-medium">{r.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No referrer data yet. This shows where your visitors come from (Google, social media, etc.)</p>
          )}
        </div>
      </div>
    </div>
  );
}
